import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Calendar, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { formatDate, formatMac } from "./constants";

const PAGE_SIZE = 20;

// labelKey is translated at render time.
const TYPE_META = {
  match: {
    labelKey: "admin.manufacturing.sessionTypes.match",
    color: "#E07A3A",
    bg: "#FEF3E7",
  },
  training: {
    labelKey: "admin.manufacturing.sessionTypes.training",
    color: "#6B7280",
    bg: "#F3F4F6",
  },
};

const SessionRow = ({ assignment, onSelect }) => {
  const { t } = useTranslation();
  const typeMeta = TYPE_META[assignment.session_type] || TYPE_META.training;
  return (
    <button
      onClick={() => onSelect(assignment.session_id)}
      className="flex w-full items-center justify-between gap-3 border-b border-[#F3F4F6] px-5 py-3 text-left transition duration-150 last:border-b-0 hover:bg-[#FAFAFA]"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
          <Calendar size={14} className="text-[#6B7280]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-[#111827]">
              {assignment.session_name ||
                t("admin.manufacturing.sessionHistory.untitled")}
            </p>
            <span
              className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{ color: typeMeta.color, backgroundColor: typeMeta.bg }}
            >
              {t(typeMeta.labelKey)}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-[#9CA3AF]">
            {t("admin.manufacturing.sessionHistory.context", {
              workspace: assignment.workspace_name,
              team: assignment.team_name,
              player: assignment.player_name,
            })}
          </p>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-4">
        <div className="text-right">
          <p className="text-xs font-medium text-[#374151]">
            {formatDate(assignment.session_date)}
          </p>
          <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
            {assignment.load != null
              ? t("admin.manufacturing.sessionHistory.impactsWithLoad", {
                  count: assignment.impacts,
                  load: assignment.load,
                })
              : t("admin.manufacturing.sessionHistory.impacts", {
                  count: assignment.impacts,
                })}
          </p>
        </div>
        <ChevronRight size={14} className="text-[#9CA3AF]" />
      </div>
    </button>
  );
};

/**
 * Sessions this unit was assigned to, newest first. Selecting one opens the
 * session's full roster of wearables (SessionWearablesModal).
 */
const SessionHistory = ({ bleMac, onSelectSession }) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const loadPage = useCallback(
    async (pageNum) => {
      // The assignments endpoint stores/keys MACs in colon form.
      const res = await betaAPI.getWearableAssignments({
        macAddress: formatMac(bleMac),
        page: pageNum,
        pageSize: PAGE_SIZE,
      });
      const pageData = res.data?.assignments || {};
      setHasMore(pageNum < (pageData.total_pages ?? 1));
      setTotalCount(pageData.total_count ?? 0);
      return pageData.results || [];
    },
    [bleMac]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPage(1);
    loadPage(1)
      .then((results) => active && setAssignments(results))
      .catch((err) => console.error("Failed to load session history", err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [loadPage]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const results = await loadPage(nextPage);
      setAssignments((prev) => [...prev, ...results]);
      setPage(nextPage);
    } catch (err) {
      console.error("Failed to load more sessions", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-5 py-3.5">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("admin.manufacturing.sessionHistory.title")}
        </h2>
        {totalCount > 0 && (
          <span className="text-[11px] text-[#9CA3AF]">
            {t("admin.manufacturing.sessionHistory.sessionCount", {
              count: totalCount,
            })}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-[#E07A3A]" />
        </div>
      ) : assignments.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#9CA3AF]">
          {t("admin.manufacturing.sessionHistory.empty")}
        </p>
      ) : (
        <>
          {assignments.map((assignment) => (
            <SessionRow
              key={assignment.id}
              assignment={assignment}
              onSelect={onSelectSession}
            />
          ))}

          {hasMore && (
            <div className="px-5 py-3">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] py-2 text-xs font-medium text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                {loadingMore && <Loader2 size={13} className="animate-spin" />}
                {t("admin.manufacturing.sessionHistory.loadOlder")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SessionHistory;
